import * as React from "react";
import {HomeInsights} from "./HomeInsights";
import {HomeResourceCard, IHomeResourceCardProps} from "./HomeResourceCard";
import {hasConsultantLevelAccess, hasResearchPageAccess} from "../../utils/sessionUtil";
import {IPlanInfo} from "../../model/ClientsAndPlans.model";
import {HomeAssetTrends} from "./HomeAssetTrends";
import {useDispatch} from "react-redux";
import {AdminActionTypes} from "../research/edit-portfolio/editPortfolioActions";
import {AnyThunkDispatch} from "../../../commonTypes";
import {HomeHeader} from "./HomeHeader.component";

export const PORTFOLIO_INSIGHTS_CARD: IHomeResourceCardProps = {
    path: "/client-reporting",
    testIdPrefix: "home-portfolio-insights",
    iconClass: "fa-light fa-chart-column",
    iconModifier: "home__resource-card-icon--portfolio-insights",
    titleKey: "header.portfolio-insights",
    titleDefault: "Portfolio Insights",
    descriptionKey: "home.portfolio-insights.description",
    descriptionDefault:
        "Reporting Hub enables our clients to access advanced analytics and 360° insights through interactive reporting.",
};

export const RESEARCH_CARD: IHomeResourceCardProps = {
    path: "/research",
    testIdPrefix: "home-research",
    iconClass: "fa-light fa-magnifying-glass",
    iconModifier: "home__resource-card-icon--research",
    titleKey: "header.manager-studio",
    titleDefault: "Manager Studio",
    descriptionKey: "home.research.description",
    descriptionDefault: "Explore Aon's ratings and analysis on your managers and strategies.",
};

export const SHARED_DOCUMENTS_CARD: IHomeResourceCardProps = {
    path: "/shared-documents",
    testIdPrefix: "home-shared-documents",
    iconClass: "fa-light fa-file-lines",
    iconModifier: "home__resource-card-icon--documents",
    titleKey: "header.shared-documents",
    titleDefault: "Shared Documents",
    descriptionKey: "home.shared-documents.description",
    descriptionDefault: "Access reports, meeting notes, and files shared by your consultant.",
};

export interface IHomeProps {
    insightsEnabled: boolean;
    sharedDocumentsEnabled: boolean;
    hasResearchAccess: boolean;
    hasPortfolioAccess: boolean;
    clientPortfolioEnabled: boolean;
    currentPlan: IPlanInfo;
    editingPlanPortfolio: boolean;
    clientReportingEnabled: boolean;
    performanceReportingEnabled: boolean;
    hasClientReports: boolean;
}

export const Home: React.FunctionComponent<IHomeProps> = (props) => {
    if (props.editingPlanPortfolio) {
        const dispatch: AnyThunkDispatch = useDispatch();
        dispatch({type: AdminActionTypes.END_EDIT_PORTFOLIO});
    }

    const renderAssetTrends = () => {
        return (props.hasPortfolioAccess && (hasConsultantLevelAccess() || props.clientPortfolioEnabled))
            ? <HomeAssetTrends />
            : null;
    };

    const showResearch = hasResearchPageAccess(
        props.hasResearchAccess,
        props.hasPortfolioAccess,
        props.clientPortfolioEnabled);

    // Mirrors the Client Reporting link visibility logic in Header.component.tsx
    const showPortfolioInsights = (props.clientReportingEnabled || hasConsultantLevelAccess())
        && (props.performanceReportingEnabled || props.hasClientReports);

    const showResources = props.sharedDocumentsEnabled || showPortfolioInsights || showResearch;

    // Determine layout class based on what's shown
    const layoutClass = props.insightsEnabled && showResources
        ? "home__content-layout"
        : props.insightsEnabled
            ? "home__content-layout"
            : "home__content-layout home__content-layout--resources-only";

    return <div className="home-common-styles home__container" data-testid="home__container">
        <HomeHeader/>
        {renderAssetTrends()}

        <section className="home__content-section">
            <div className="home__content-inner">
                <div className={layoutClass}>
                    {props.insightsEnabled && <HomeInsights />}
                    {showResources && (
                        <div className="home__resources-panel">
                            {showPortfolioInsights && <HomeResourceCard {...PORTFOLIO_INSIGHTS_CARD} />}
                            {showResearch && <HomeResourceCard {...RESEARCH_CARD} />}
                            {props.sharedDocumentsEnabled && <HomeResourceCard {...SHARED_DOCUMENTS_CARD} />}
                        </div>
                    )}
                </div>
            </div>
        </section>
    </div>;
};
